import { useEffect, useState } from 'react';

import { api } from '@/app/api';
import { UiButton, UiInput } from '@shared/common/ui';

const numberFormatter = new Intl.NumberFormat('ru-RU');
const monthFormatter = new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' });

function currentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(month) {
  return monthFormatter.format(new Date(`${month}-01T12:00:00`));
}

export function ProfileNewClientsWidget() {
  const [month, setMonth] = useState(currentMonth);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requestVersion, setRequestVersion] = useState(0);

  useEffect(() => {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) {
      setLoading(false);
      setStatistics(null);
      setError('Выберите месяц для расчёта');
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setStatistics(null);
    api.get('/owner/profile/new-clients', { params: { month }, signal: controller.signal })
      .then(({ data }) => setStatistics(data.statistics))
      .catch((err) => {
        if (err.code !== 'ERR_CANCELED') {
          setError(err.response?.data?.message ?? 'Не удалось загрузить данные о новых клиентах');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [month, requestVersion]);

  const change = statistics?.change ?? 0;
  const changeText = change > 0 ? `+${numberFormatter.format(change)}` : numberFormatter.format(change);
  const percent = statistics?.changePercent;
  const percentText = percent === null
    ? 'Процент не рассчитывается: год назад не было новых клиентов'
    : `${percent > 0 ? '+' : ''}${numberFormatter.format(percent)}% к прошлому году`;

  return (
    <div className="form-card profile-new-clients">
      <div className="profile-new-clients__header">
        <h2>Новые клиенты</h2>
        <UiInput
          label="Месяц"
          type="month"
          min="0002-01"
          max="9999-12"
          value={month}
          onChange={(event) => setMonth(event.target.value)}
        />
      </div>

      {loading ? (
        <div className="placeholder" role="status">Считаем первые посещения…</div>
      ) : error ? (
        <div className="profile-new-clients__error">
          <div className="form-note form-note--error" role="alert">{error}</div>
          {month && <UiButton type="button" variant="ghost" size="sm" onClick={() => setRequestVersion((value) => value + 1)}>Повторить</UiButton>}
        </div>
      ) : statistics ? (
        <div className="profile-new-clients__content" aria-live="polite">
          <div className="profile-new-clients__main">
            <strong>{numberFormatter.format(statistics.count)}</strong>
            <span>новых клиентов за {monthLabel(statistics.month)}</span>
          </div>
          <div className="profile-new-clients__comparison">
            <div>
              <span>{monthLabel(statistics.previousYearMonth)}</span>
              <strong>{numberFormatter.format(statistics.previousYearCount)}</strong>
            </div>
            <div className={change > 0 ? 'is-positive' : change < 0 ? 'is-negative' : ''}>
              <span>Изменение</span>
              <strong>{changeText}</strong>
              <small>{percentText}</small>
            </div>
          </div>
          <p className="profile-new-clients__note">
            Считаем каждого клиента один раз — по первой отметке присутствия во всех филиалах.
            {statistics.month === currentMonth() ? ' Текущий месяц ещё не завершён.' : ''}
          </p>
        </div>
      ) : null}
    </div>
  );
}
